import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { sucursalService, Sucursal, Localidad, Provincia, Pais } from '../services/sucursalService';
import { clienteService } from '../services/clienteService';
import { uploadArchivo } from '../services/archivoService';
import ArchivoMedia from '../components/ArchivoMedia';
import Notification from '../components/Notification';

type AssignedUser = {
  id: number;
  alias: string;
  nombre?: string;
  apellido?: string;
  email?: string;
};

const formatUserLabel = (u: AssignedUser) =>
  [u.apellido, u.nombre].filter(Boolean).join(', ') || u.alias || u.email || String(u.id);

const SucursalFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const [formData, setFormData] = useState<Partial<Sucursal>>({
    nombre: '',
    direccion: '',
    telefono: '',
    mail: '',
    activo: true,
    clienteId: 0,
    localidadId: 0,
  });

  const [paises, setPaises] = useState<Pais[]>([]);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [mapaFile, setMapaFile] = useState<File | null>(null);
  const [selectedPaisId, setSelectedPaisId] = useState<number>(0);
  const [selectedProvinciaId, setSelectedProvinciaId] = useState<number>(0);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);

  useEffect(() => {
    const clienteId = Number(formData.clienteId) || 0;
    if (!clienteId) {
      setAssignedUsers([]);
      return;
    }
    let cancelled = false;
    clienteService
      .getUsuariosAsignados(clienteId)
      .then((users) => {
        if (!cancelled) setAssignedUsers(users || []);
      })
      .catch(() => {
        if (!cancelled) setAssignedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.clienteId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [paisesData, clientesData] = await Promise.all([
        sucursalService.getPaises(),
        sucursalService.getClientes(),
      ]);

      setPaises(paisesData);
      setClientes(clientesData);

      if (isEditing && id) {
        const sucursalData = await sucursalService.getById(parseInt(id));
        setFormData(sucursalData);

        const loc = (sucursalData as any).localidad;
        const provincia = loc?.provincia;
        const paisId = provincia?.paisId ?? provincia?.pais?.id ?? 0;
        const provinciaId = loc?.provinciaId ?? provincia?.id ?? 0;
        const localidadId = sucursalData.localidadId || loc?.id || 0;

        if (paisId) {
          setSelectedPaisId(paisId);
          const provinciasData = await sucursalService.getProvinciasByPais(paisId);
          setProvincias(provinciasData);
        }
        if (provinciaId) {
          setSelectedProvinciaId(provinciaId);
          const localidadesData = await sucursalService.getLocalidadesByProvincia(provinciaId);
          setLocalidades(localidadesData);
        }
        if (localidadId) {
          setFormData((prev) => ({ ...prev, ...sucursalData, localidadId }));
        }
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos',
      });
    } finally {
      setLoading(false);
    }
  }, [id, isEditing]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const numericFields = new Set(['clienteId', 'localidadId']);
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : numericFields.has(name)
            ? parseInt(String(value), 10) || 0
            : value,
    }));
  };

  const handlePaisChange = async (paisId: number) => {
    setSelectedPaisId(paisId);
    setSelectedProvinciaId(0);
    setProvincias([]);
    setLocalidades([]);
    setFormData((prev) => ({ ...prev, localidadId: 0 }));
    if (!paisId) return;
    try {
      const provinciasData = await sucursalService.getProvinciasByPais(paisId);
      setProvincias(provinciasData);
    } catch {
      setNotification({ type: 'error', message: 'Error al cargar provincias' });
    }
  };

  const handleProvinciaChange = async (provinciaId: number) => {
    setSelectedProvinciaId(provinciaId);
    setLocalidades([]);
    setFormData((prev) => ({ ...prev, localidadId: 0 }));
    if (!provinciaId) return;
    try {
      const localidadesData = await sucursalService.getLocalidadesByProvincia(provinciaId);
      setLocalidades(localidadesData);
    } catch {
      setNotification({ type: 'error', message: 'Error al cargar localidades' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload: Partial<Sucursal> = {
        ...formData,
        clienteId: Number(formData.clienteId) || 0,
        localidadId: Number(formData.localidadId) || 0,
      };
      if (mapaFile) {
        const uploaded = await uploadArchivo(mapaFile, 'sucursales/mapas');
        payload.mapaDePuestosArchivoId = uploaded.id;
      }

      if (isEditing && id) {
        await sucursalService.update(parseInt(id), payload);
        setNotification({
          type: 'success',
          message: 'Sucursal actualizada correctamente',
        });
      } else {
        await sucursalService.create(payload as Omit<Sucursal, 'id'>);
        setNotification({
          type: 'success',
          message: 'Sucursal creada correctamente',
        });
      }

      setTimeout(() => {
        navigate('/sucursales');
      }, 1500);
    } catch (error) {
      console.error('Error al guardar sucursal:', error);
      setNotification({
        type: 'error',
        message: 'Error al guardar la sucursal',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/sucursales')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {isEditing ? 'Editar Sucursal' : 'Nueva Sucursal'}
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                  {isEditing
                    ? 'Modifica la información de la sucursal'
                    : 'Completa la información de la nueva sucursal'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Información General
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre || ''}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Nombre de la sucursal"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cliente *
                  </label>
                  <select
                    name="clienteId"
                    value={formData.clienteId || ''}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Seleccionar cliente</option>
                    {clientes.map((cliente) => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dirección
                  </label>
                  <input
                    type="text"
                    name="direccion"
                    value={formData.direccion || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Dirección de la sucursal"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="+54 11 1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="mail"
                    value={formData.mail || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="sucursal@empresa.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    País
                  </label>
                  <select
                    value={selectedPaisId || ''}
                    onChange={(e) => handlePaisChange(parseInt(e.target.value, 10) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Seleccionar país</option>
                    {paises.map((pais) => (
                      <option key={pais.id} value={pais.id}>
                        {pais.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Provincia
                  </label>
                  <select
                    value={selectedProvinciaId || ''}
                    onChange={(e) => handleProvinciaChange(parseInt(e.target.value, 10) || 0)}
                    disabled={!selectedPaisId}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Seleccionar provincia</option>
                    {provincias.map((provincia) => (
                      <option key={provincia.id} value={provincia.id}>
                        {provincia.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Localidad *
                  </label>
                  <select
                    name="localidadId"
                    value={formData.localidadId || ''}
                    onChange={handleChange}
                    required
                    disabled={!selectedProvinciaId}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Seleccionar localidad</option>
                    {localidades.map((localidad) => (
                      <option key={localidad.id} value={localidad.id}>
                        {localidad.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mapa de puestos
                  </label>
                  {formData.mapaDePuestosArchivoId ? (
                    <div className="mb-3">
                      <ArchivoMedia
                        archivoId={formData.mapaDePuestosArchivoId}
                        alt="Mapa de puestos"
                        className="max-h-48 rounded border border-gray-200 dark:border-gray-600"
                      />
                    </div>
                  ) : null}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => setMapaFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-700 dark:text-gray-300"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="activo"
                      checked={formData.activo || false}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-500 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sucursal activa
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center mb-4">
                <UserGroupIcon className="w-5 h-5 text-gray-500 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Usuarios asignados
                </h2>
              </div>
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                Los usuarios se asignan por cliente. Si querés cambiarlos, andá a Seguridad →
                Gestión de usuarios.
              </div>
              {assignedUsers.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formData.clienteId
                    ? 'No hay usuarios asignados a este cliente.'
                    : 'Seleccioná un cliente para ver los usuarios asignados.'}
                </p>
              ) : (
                <ul className="space-y-2">
                  {assignedUsers.map((u) => (
                    <li
                      key={u.id}
                      className="rounded-md border border-gray-200 dark:border-gray-600 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/50"
                    >
                      {formatUserLabel(u)}
                      {u.email ? (
                        <span className="ml-2 text-gray-500 dark:text-gray-400">{u.email}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/sucursales')}
                className="flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                <XMarkIcon className="w-5 h-5 mr-2" />
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <CheckIcon className="w-5 h-5 mr-2" />
                )}
                {isEditing ? 'Guardar cambios' : 'Crear sucursal'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default SucursalFormPage;
